import React, { useEffect, useRef } from 'react';
import { useMetasiteError } from '../context/errors';
import { useBiLogger } from '../hooks/useBiLogger/useBiLogger';
import { GeneralError } from '../components/GeneralError/GeneralError';
import { SitePreviewWrapper } from '../features/sitePreview/SitePreviewWrapper';
import { useMsIdAtomValue, useDsUserInsights, useServices } from '../context';
import { Box, Loader } from '@wix/design-system';
import { useGTM } from '../hooks/useGTM';
import { useLocalStorageSync } from '../hooks/useLocalStorageSync';
import { DsBuilderMethod } from '../types';
import { useQuerySitesLoading } from '../features/sitePreview/context/querySitesContext';

type SitePreviewPageProps = {};

export const SitePreview: React.FC<SitePreviewPageProps> = () => {
  const metasiteError = useMetasiteError();
  const msId = useMsIdAtomValue();
  const isQuerySitesLoading = useQuerySitesLoading();
  const { chatServiceDs: chatService } = useServices();
  const { isLoading: isLocalStorageSyncing } = useLocalStorageSync();
  const userInsights = useDsUserInsights();
  const currentStoreName = userInsights?.storeName?.summary;

  const biLogger = useBiLogger();
  const { trackPreviewPageView } = useGTM();
  const hasTracked = useRef(false);

  // Tracking events - only run once when ready
  useEffect(() => {
    if (isLocalStorageSyncing || hasTracked.current) {
      return;
    }

    // Mark as tracked to prevent duplicate events
    hasTracked.current = true;

    // Existing BI logging
    biLogger.pageView(DsBuilderMethod[DsBuilderMethod.SITE_PREVIEW]);

    // GTM tracking for preview page
    trackPreviewPageView(currentStoreName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocalStorageSyncing, currentStoreName]); // Re-run if syncing status or store name changes, but ref prevents duplicates. Functions omitted to prevent re-runs.

  // Data fetching - re-run when dependencies change
  useEffect(() => {
    if (isLocalStorageSyncing) {
      return;
    }

    const fetchQuerySites = async () => {
      await chatService.querySites();
    };

    fetchQuerySites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocalStorageSyncing]); // chatService is stable and doesn't need to be in dependencies

  if (isLocalStorageSyncing || isQuerySitesLoading || !msId) {
    return (
      <Box height="100%" width="100%" align="center" verticalAlign="middle">
        <Loader />
      </Box>
    );
  }

  if (metasiteError) {
    return <GeneralError />;
  }

  return <SitePreviewWrapper />;
};
